import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject, Fragment}  from 'react';

import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation  } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import MoreIcon from '@mui/icons-material/More';

import SelectAllIcon from '@mui/icons-material/SelectAll';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';

import NumberFormat from 'react-number-format';
import TextFieldRight from 'components/ui/TextFieldRight';

import { IEntity, IResult } from 'library/interface';

import useRegistrationMarkService from './services/RegistrationMark';
import useRegistrationService from './services/Registration';

import useUtils from 'library/utils';

import { IRegistrationMark, IRegistrationMarkCompetence, ISchoolYearClassSubjectMark, 
    defaultRegistrationMark, defaultRegistrationMarkCompetence, defaultSchoolYearClassSubjectMark,  } from './models/RegistrationMark';

import useEntityService, {useBasicFilterEntity, useBasicFilterFeatureDescription} from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom, 
      isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';

import externalLibraryService from 'features/configuration/services/ExternalLibrary';
import {defaultExternalLibrary, defaultExternalLibraryClass, IExternalLibrary, IExternalLibraryClass} from 'features/configuration/models/ExternalLibrary';


import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { isFalsy } from 'utility-types';
import { FormDialog } from 'components/ui/FormDialog';
import { Checkbox, Divider, FormControlLabel, FormHelperText, IconButton, InputAdornment, Link, Typography } from '@mui/material';
import IEnumeration, { IEnumerationItem, Enum_REGISTRATION_STATUS, Enum_STUDY_TYPE, Enum_DAY_WEEK,
  Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_TEACHER_STATUS, Enum_SUBJECT,
  Enum_ABSENCE_TYPE, Enum_ABSENCE_PURPOSE } from 'features/configuration/models/Enumeration';

import useEnumerationService, { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';

import useTeacherService from 'features/setup/services/Teacher';

import { BasicTextFilterForm, defaultBasicTextFilterProps } from 'components/ui/BasicTextFilterForm';
import { ISchoolYearClass, ISchoolYearClassSubject, ISchoolYearClassSubjectExamSchedule, 
  ISchoolYearClassTemplate, ISchoolYearPeriod, ISchoolYearPeriodExam, ISchoolYearTemplateSubjectExamSchedule } from 'features/setup/models/SchoolYear';
import useSchoolYearService, { useBasicFilterSchoolYearClass } from 'features/setup/services/SchoolYear';
import { DatePicker } from '@mui/x-date-pickers';
import { typographyGroupBoxStyling } from 'themes/commonStyles';

import { debounce } from 'lodash';
import EnhancedTable from 'components/ui/EnhancedTable';
import BasicImport2Form from 'components/ui/BasicImport2Form';
import { exportToExcelExMultipleSheets } from 'library/xlsx-export';
import { useDownloadTextFile } from 'library/useDownloadTextFile';
import { ISchoolYearClassDisciplinaryMark, IStudentDisciplinary, defaultSchoolYearClassDisciplinaryMark } from './models/Registration';

//import { useBasicFilterApplicationQuery } from 'features/setup/services/ApplicationQuery';



export const AbsenceDisciplinaryMarkForm: FC<ISchoolYearClassDisciplinaryMark> = (props: ISchoolYearClassDisciplinaryMark = defaultSchoolYearClassDisciplinaryMark) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {id} = useParams();

  const {findDuplicate, findDuplicate2, findDuplicate3, parseDate, parseTime} = useUtils();

  const {language: lg, schoolYears, isTeacher, teacherId, roleEntities} = useRecoilValue(currentUserSessionAtom);
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const { displayDownload : displayDownloadErrorLogs } = useDownloadTextFile();

  const { enqueueSnackbar } = useSnackbar();

  const {retrieveEntity, retrieveData, openEntityActionDrawer, checkEntitySaveAuthorization} = useEntityService();
  const {saveSchoolYearClassSubjectMark } = useRegistrationMarkService();

  const {saveSchoolYearClassDisciplinaryMark } = useRegistrationService();

  const {getEnumerationItemsByEnumerationCodes,getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();
  
  // const { getSchoolYearClassTemplates, getSchoolYearClassRegistrationMarks, getSchoolYearClassSubjects, getSchoolYearClassSubjectsByPeriodExamTeacher,
  //   getSchoolYearPeriods, getSchoolYearPeriodExams, getSchoolYearTemplateSubjectExamSchedules,
  //   getSchoolYearClassSubjectExamSchedules, getBasicStudentDisciplinaries ,saveSchoolYearClassDisciplinaryMark } = useSchoolYearService();
  

  const schoolYearServiceRef = useRef(useSchoolYearService());
  const registrationServiceRef = useRef(useRegistrationService());


  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  // const basicFilterApplicationQuery = useBasicFilterApplicationQuery( 
    
  //   async (event: React.MouseEvent<unknown>, row: IApplicationQuery) => {
  //     const {id, name } = row;
      
  //     setIsSearchBoxShow(false);
  //     _setId(row.id);
  //   }
  // );

  const [openClassFilter, setOpenClassFilter] = useState(false);
  const basicFilterSchoolYearClass = useBasicFilterSchoolYearClass( 
    async (event: React.MouseEvent<unknown>, row: ISchoolYearClass) => {
           
        //const schoolYearClass = await retrieveEntity('SchoolYearClass',row.id) as ISchoolYearClass;
        const {id, studyType, name, schoolYearClassTemplateId,
          classTemplateName, studyLanguage,
          fullTeacherName, substituteTeacherName,
          schoolYearId
          } = row;  
          
        const data = getValues();
        reset({...data, schoolYearClassId: id, 
              schoolYearId, 
              
              schoolYearClassName: name, studyType, 
              fullTeacherName, substituteTeacherName, classTemplateName, studyLanguage,
              studentDisciplinaries: []
             });

        setValue('schoolYearId', schoolYearId); 
        
        if(isTeacher) {
          setSchoolYearClassSubjects([]);
        } else {
          const arr = await schoolYearServiceRef.current.getSchoolYearClassSubjects(id); // schoolYearClassId
          setSchoolYearClassSubjects([...arr]);                    
        }

        const arr2 = await schoolYearServiceRef.current.getSchoolYearClassSubjectExamSchedules(id, 0 ,0);
          setSchoolYearClassSubjectExamSchedules(arr2);

        setOpenClassFilter(false);     
      }
  );


  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration( 
      (event: React.MouseEvent<unknown>, row: IEnumeration) => {
          const {id, name, description} = row;
          
          
      }
  );

  

  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity( 
      (event: React.MouseEvent<unknown>, row: IEntity) => {
          const {name, description} = row;

          
      }
  );
  
    
  const emptyFunc = (obj: any) => {}

  const methods = useForm<ISchoolYearClassDisciplinaryMark>({defaultValues:defaultSchoolYearClassDisciplinaryMark});
  const { register, setValue ,getValues, watch, reset ,handleSubmit, setFocus ,control , formState: { errors } } = methods;

  const watchSchoolYearId = watch('schoolYearId');
  const watchSchoolYearClassId = watch('schoolYearClassId');
  const watchSchoolYearPeriodId = watch('schoolYearPeriodId');
  // const watchSchoolYearPeriodExamId = watch('schoolYearPeriodExamId');
  // const watchSchoolYearClassSubjectId = watch('schoolYearClassSubjectId');
 
  const watchSchoolYearClassTemplateId = watch('schoolYearClassTemplateId');

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<ISchoolYearClassDisciplinaryMark>,Error,ISchoolYearClassDisciplinaryMark>(
      registrationServiceRef.current.saveSchoolYearClassDisciplinaryMark, {   
        onSuccess: async (data: IResult<ISchoolYearClassDisciplinaryMark>) => {
          enqueueSnackbar( t('Operation done !!!'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          setIsSaveLoading(false);
          // const schoolYearClassSubjectExamSchedule = schoolYearClassSubjectExamSchedules.find(
          //     x => x.schoolYearClassId === data.data.schoolYearClassId &&
          //     x.schoolYearTemplateSubjectExamScheduleId === data.data.schoolYearTemplateSubjectExamScheduleId );

          // setSchoolYearClassSubjectExamSchedules(schoolYearClassSubjectExamSchedules.map( x => {

          //   return {...x,
          //       id: isFalsy(schoolYearClassSubjectExamSchedule)? x.id : schoolYearClassSubjectExamSchedule.id,
          //       status: isFalsy(schoolYearClassSubjectExamSchedule)? x.status : schoolYearClassSubjectExamSchedule.status
          //   }
          // }));
          // const arr = await getSchoolYearClassSubjectExamSchedules(data.data.schoolYearClassId, 0 ,0);
          // setSchoolYearClassSubjectExamSchedules(arr);

          // _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);
          
          // queryClient.invalidateQueries(['SchoolYearClassSubjectMark',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });
    
    const {data: _data, refetch} = useQuery<ISchoolYearClassSubjectMark>(['SchoolYearClassSubjectMark', _id], () => retrieveEntity('SchoolYearClassSubjectMark',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

    const {data: schoolYearClassTemplate } = useQuery<ISchoolYearClassTemplate>(['SchoolYearClassSubjectMark', 'SchoolYearClassTemplate', watchSchoolYearClassTemplateId], 
          () => retrieveEntity('SchoolYearClassTemplate',watchSchoolYearClassTemplateId), {refetchOnWindowFocus: false ,enabled: watchSchoolYearClassTemplateId>0 } );

    const {data: enumItems} = useQuery<IEnumerationItem[]>( ['EnumerationItems', 'SchoolYearClassTemplate'], () => getEnumerationItemsByEnumerationCodes
      ( [Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_REGISTRATION_STATUS, Enum_TEACHER_STATUS, Enum_DAY_WEEK,
            Enum_STUDY_TYPE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_SUBJECT, 
            Enum_ABSENCE_TYPE, Enum_ABSENCE_PURPOSE ] ));

    const [schoolYearPeriods, setSchoolYearPeriods] = useState<ISchoolYearPeriod[]>([]);
    const [schoolYearPeriodExams, setSchoolYearPeriodExams] = useState<ISchoolYearPeriodExam[]>([]);

    const [schoolYearClassSubjects, setSchoolYearClassSubjects] = useState<ISchoolYearClassSubject[]>([]);

    const [schoolYearClassSubjectExamSchedules, setSchoolYearClassSubjectExamSchedules] = useState<ISchoolYearClassSubjectExamSchedule[]>([]);

    //const [schoolYearTemplateSubjectExamSchedules, setSchoolYearTemplateSubjectExamSchedules] = useState<ISchoolYearTemplateSubjectExamSchedule[]>([]);

    // const [currentLibrary, setCurrentLibrary] = useState<IExternalLibrary>(defaultExternalLibrary);
    // const [currentLibraryClass, setCurrentLibraryClass] = useState<IExternalLibraryClass>(defaultExternalLibraryClass);

    // const [dbmsType, setDbmsType] = useState<string>('');

    // const [applicationQueryIndex, setApplicationQueryIndex] = useState<number>(-1);
    // const [applicationQueryParameterIndex, setRegistrationMarkIndex] = useState<number>(-1);

    // const [openAppQueryParamDialog, setOpenAppQueryParamDialog] = useState<boolean>(false);
 
    const handleClassNavigationClick = (event: any) => {
        if(!roleEntities.some(re => re.entityName === 'SchoolYearClass')) return;
        
        navigate(`/SchoolYearClass/${getValues().schoolYearClassId}`);
      };

   const inputLogin = useRef();
   const cellKeyDownMark = (e: React.KeyboardEvent<HTMLInputElement>, row: IRegistrationMark,index: number) => {
    
    if( !['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key) )
      return;

    const cellId = 'mark';
    const rowsPathNameDef = 'studentDisciplinaries';
    const len = getValues().studentDisciplinaries.length;

    const idx = ['Enter', 'ArrowDown'].includes(e.key) ? 
                  Math.min(index+1, len-1) : Math.max(index-1, 0);
    
    const elt = document.getElementById(`${rowsPathNameDef}-id-${cellId}-${idx}`); // This is how id is set in ArrayFeldTableEx
    if(isFalsy(elt))
      return;

    elt!.focus();
    (elt! as HTMLInputElement).select();    
  }

  const cellEditableJustifiedAbsence = (row: IStudentDisciplinary, cellId: keyof IStudentDisciplinary) => {      
    return row.editJustifiedAbsence;
  }

  const cellEditableUnjustifiedAbsence = (row: IStudentDisciplinary, cellId: keyof IStudentDisciplinary) => {      
    return row.editUnjustifiedAbsence;
  }

  const cellEditableStudentDisciplinary = (row: IStudentDisciplinary, cellId: keyof IStudentDisciplinary) => {      
    return true;
  }

  const cellAllowedMark = (row: IStudentDisciplinary, valueMark: number) => {
    // if(valueMark > getValues().maxMark)
    //   enqueueSnackbar( `${t('Invalid mark, the mark must be less than the max allowed' )} : ${getValues().maxMark} !!!`, { variant: 'warning',
    //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 

    // const allowed = valueMark >= 0 && valueMark <= getValues().maxMark;
    // debouncedMark();

    // return allowed;
    return true;
  }

  const textDecorationMark = (row: IStudentDisciplinary, index: number) : string => {

    // if(!row.markExist) return '';
    
    // if(isFalsy(schoolYearClassTemplate)) return 'line-through';
    
    // const schoolYearClassSubject = schoolYearClassSubjects.find( s => s.id === watchSchoolYearClassSubjectId);
    // if(isFalsy(schoolYearClassSubject)) return 'line-through';
    
    // const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
    //         s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
    // if(isFalsy(schoolYearTemplateSubject)) return 'line-through';
    
    // const schoolYearTemplateSubjectExamSchedule =  schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find( 
    //   e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId );
    
    // if(isFalsy(schoolYearTemplateSubjectExamSchedule)) return 'line-through';
    
    // if( schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length !== 
    //     row.registrationMarkCompetences.length) return 'line-through';
    
    // if(schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length === 0)
    //   return '';

    // const { maxMark } = getValues();
   
    // if(row.registrationMarkCompetences.some(x => x.maxMark <= 0)) return 'line-through';

    // if(row.registrationMarkCompetences.some(x => x.maxMark < x.mark)) return 'line-through double';

    // const sumMark = row.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.mark , 0);
    // const sumMaxMark = row.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.maxMark , 0);
    
    // const markCompetence = (sumMark*maxMark)/sumMaxMark;

    // if( Math.abs(markCompetence-row.mark) > 0.0001 ) return 'line-through';
    //const registrationMarkCompetences = row.registrationMarkCompetences;

    return '';
  }  

  useEffect(() => {
    
    const subscription = watch( async (value, { name, type }) => {
      
      if( name?.startsWith('registrationMarks') && name?.endsWith('markExist') ) {    
        
        debouncedMark();                          
      } 
    });  

    return () => subscription.unsubscribe();
  }, [watch]);

  const debouncedMark = useRef(
    debounce( () => {
      //const sumAmount = sum( getValues().tellerOperationBillings.map( ({amount}) => amount ) );
      //setValue('amount', sumAmount);

      const marks = getValues().studentDisciplinaries.map( x => x.conductGrade).sort((a,b)=>b-a) ; // desc.
      const markSet = new Set(marks);
      const len = getValues().studentDisciplinaries.length;

      markSet.forEach(function(conductGrade) {

        for(var i=0; i< len; i++) {
          const studentDisciplinary = getValues().studentDisciplinaries[i];

          if(studentDisciplinary.conductGrade !== conductGrade) continue;

          const calcRank = getValues().studentDisciplinaries.filter( x => /*x.markExist &&*/ x.conductGrade > conductGrade).length;
            
          // (refUpdateStudentDisciplinary.current??emptyFunc)(i, {
          //   ...studentDisciplinary, 
          //   //mark: registrationMark.markExist? mark : 0,
          //   rank: registrationMark.markExist? calcRank+1 : 0,

          //   registrationMarkCompetences: registrationMark.registrationMarkCompetences.map(c => ({...c, 
          //       markExist: registrationMark.markExist? c.markExist : false,
          //       mark : registrationMark.markExist && c.markExist ? c.mark : 0
          //     }))
          // });   
        }

      });

      
    }, 1500)
  ).current;

 
  const [headStudentDisciplinaryCells, setHeadStudentDisciplinaryCells]  = useState<HeadCell<IStudentDisciplinary>[]>([]);
  useEffect(() => {
    setHeadStudentDisciplinaryCells([            
      
      {id:lg.includes('en')?'fullNameEN':'fullNameFR', label : t('Student'),  display: true, type: 'string', width: 50},
     
      {id:'conductGrade', label : t('Conduct mark'),  display: true, type: 'numeric', width: 20, decimalScale: 2,
          isEditable: cellEditableStudentDisciplinary },
      {id:'justifiedAbsence', label : t('Justified abs.'),  display: true, type: 'numeric', 
          isEditable: cellEditableJustifiedAbsence,  isAllowed: cellAllowedMark, width: 15},
      {id:'unjustifiedAbsence', label : t('Unjustified abs.'),  display: true, type: 'numeric', 
         isEditable: cellEditableUnjustifiedAbsence,  isAllowed: cellAllowedMark, width: 15},

      // {id:'mark', label : t('Mark'),  display: true, type: 'numeric', width: 10, decimalScale: 2, 
      //   isEditable: cellEditableRegistrationMark,  isAllowed: cellAllowedMark,textDecoration: textDecorationMark,
      //   handleKeyDown: cellKeyDownMark, },
      // {id:'markExist', label : t('Exist ?'),  display: true, type: 'boolean', width: 10, },
      // {id:'rank', label : t('Rank'),  display: true, type: 'numeric', width: 10 },
    ]  )
  }, [t,i18n, watchSchoolYearPeriodId/*, watchSchoolYearPeriodExamId, watchSchoolYearClassSubjectId*/])

  const refAppendStudentDisciplinaries = useRef<(value: Partial<FieldArray<ISchoolYearClassDisciplinaryMark>> | Partial<FieldArray<ISchoolYearClassDisciplinaryMark>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateStudentDisciplinary = useRef<(index: number,value: Partial<FieldArray<ISchoolYearClassDisciplinaryMark>> ) => void>(null);
  const refRemoveStudentDisciplinary = useRef<(index: number ) => void>(null);

  const [defineAllMark, setDefineAllMark] = useState<boolean>(false);
  const [selectedRegistrationsIndex, setSelectedRegistrationsIndex] = useState<number[]>([]);
  const [openSameMarkDialog, setOpenSameMarkDialog] = useState<boolean>(false);
  const [sameMark, setSameMark] = useState<number>(0);
  const handleSetMarkForSelected = (event: any) => {  
    if(isFalsy(getValues().studentDisciplinaries.length)) return;
    if(isFalsy(selectedRegistrationsIndex.length)) return;
    //if( !['00','95'].includes( getValues().schoolYearClassSubjectExamScheduleStatus) ) return;

    setDefineAllMark(false);
    setOpenSameMarkDialog(true);
  }

  const handleSetMarkForAll = (event: any) => { 
    if(isFalsy(getValues().studentDisciplinaries.length)) return;
    //if( !['00','95'].includes( getValues().schoolYearClassSubjectExamScheduleStatus) ) return;

    setDefineAllMark(true);
    setOpenSameMarkDialog(true);
  }

  const handleSetMarkOk = (event: any) => { 
    // if(sameMark < 0 || sameMark > getValues().maxMark){
    //     enqueueSnackbar( `${t('Invalid mark, the mark must be less than the max allowed' )} : ${getValues().maxMark} !!!`, { variant: 'warning',
    //       anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
    //     return;
    // }

    if(defineAllMark) {
      for(var k=0; k<getValues().studentDisciplinaries.length; k++) {
        const studentDisciplinary = getValues().studentDisciplinaries[k];
        (refUpdateStudentDisciplinary.current??emptyFunc)(k, { ...studentDisciplinary, conductGrade: sameMark }); 
      }
    } else { 
      for(var i=0; i<selectedRegistrationsIndex.length; i++) {
        const k = selectedRegistrationsIndex[i];
        if( k<0 || k >= getValues().studentDisciplinaries.length ) continue;

        const studentDisciplinary = getValues().studentDisciplinaries[k];
        (refUpdateStudentDisciplinary.current??emptyFunc)(k, { ...studentDisciplinary, conductGrade: sameMark }); 
      }
      
    }    

    debouncedMark();
    setOpenSameMarkDialog(false);
  }

  
  const handleDownloadMarks = (event: any) => {
    if(isFalsy(getValues().studentDisciplinaries.length)) return;

    exportToExcelExMultipleSheets(
      [
        { items: getValues().studentDisciplinaries, sheetName: `${t('Mark')}`, 
              columns2Export: ['firstName','lastName','mark'] },
        // { items: getValues().registrationMarks.flatMap( ({registrationMarkCompetences}) => registrationMarkCompetences ), 
        //       sheetName: `${t('Mark')}-${t('Competences')}`, 
        //       columns2Export: ['firstName','lastName','schoolYearTemplateSubjectCompetenceTitle','mark'] }
      ],
      //`${t('Marks')}-${getValues().schoolYearClassName}-${refSchoolYearPeriodExamName.current}-${getValues().schoolYearTemplateSubjectLongName}`
      `${t('Marks')}-${getValues().schoolYearClassId}-${refSchoolYearPeriodExamName.current}`
    );
  }

  const [markObjecBasicImport2, setMarkObjecBasicImport2] = useState<
    {first: IRegistrationMark[], second: IRegistrationMarkCompetence[]}>( { first: [], second: [] } );

  const [openBasicImport2Form, setOpenBasicImport2Form] = useState<boolean>(false);
  const handleLoadMarks = (event: any) => {
    if(isFalsy(getValues().studentDisciplinaries.length)) return;
    //if( !['00','95'].includes( getValues().schoolYearClassSubjectExamScheduleStatus) ) return;

    setOpenBasicImport2Form(true);
  }

  const handleOKBasicImport2Marks = (event: any) => {

    if(isFalsy(getValues().studentDisciplinaries.length)) return; 
    //if( !['00','95'].includes( getValues().schoolYearClassSubjectExamScheduleStatus) ) return;
    
    if(isFalsy(schoolYearClassTemplate)) return;
    
    // const schoolYearClassSubject = schoolYearClassSubjects.find( s => s.id === watchSchoolYearClassSubjectId);
    // if(isFalsy(schoolYearClassSubject)) return;
    
    // const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
    //         s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
    // if(isFalsy(schoolYearTemplateSubject)) return;
    
    // const schoolYearTemplateSubjectExamSchedule =  schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find( 
    //         e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId );
            
    // if(isFalsy(schoolYearTemplateSubjectExamSchedule)) return;

    // const sheetRegistrationMarks = markObjecBasicImport2.first; 
    // const sheetRegistrationMarkCompetences = markObjecBasicImport2.second; 
    
    // const duplicateRegistrationMark = findDuplicate2(sheetRegistrationMarks, 'firstName', 'lastName');
    // if(!isFalsy(duplicateRegistrationMark)) {
    //   enqueueSnackbar( `${duplicateRegistrationMark.firstName} ${duplicateRegistrationMark.lastName} ${t('is more than one, check that and others.')}`, 
    //     { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 });
    //     return;
    // }
    
    // const duplicateRegistrationMarkCompetence = findDuplicate3(sheetRegistrationMarkCompetences, 'firstName', 'lastName','schoolYearTemplateSubjectCompetenceTitle');
    // if(!isFalsy(duplicateRegistrationMarkCompetence)) {
    //   enqueueSnackbar( `${duplicateRegistrationMarkCompetence.firstName} ${duplicateRegistrationMarkCompetence.lastName} 
    //       ${duplicateRegistrationMarkCompetence.schoolYearTemplateSubjectCompetenceTitle} ${t('is more than one, check that and others.')}`, 
    //     { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 });
    //     return;
    // }

    
    const {studentDisciplinaries} = getValues();

    const errorLogs = [] as string[];

    // for(const regMark of sheetRegistrationMarks.filter(r => !isFalsy(r.firstName) || !isFalsy(r.lastName)) ) {

      
    //   const {firstName, lastName, mark} = regMark;
    //   const indexRegistrationMark = registrationMarks.findIndex(r => 
    //                         r.firstName.trim() === firstName.trim() && r.lastName.trim() === lastName.trim());
                            
    //   if(indexRegistrationMark>=0) {
    //     const registrationMark = registrationMarks[indexRegistrationMark];
        
    //     setValue(`registrationMarks.${indexRegistrationMark}.mark`, mark);

    //     const registrationMarkCompetences = schoolYearTemplateSubjectExamSchedule?.schoolYearTemplateSubjectExamScheduleCompetences.map(
    //       (c) => {
            
    //         const markCompetence = registrationMark.registrationMarkCompetences.find(x => x.schoolYearTemplateSubjectExamScheduleCompetenceId === c.id);

    //         return {...c, 
    //           id: isFalsy(markCompetence) ? 0 : markCompetence!.id,

    //           registrationMarkId: registrationMark.id, status: 'N', 
    //           currentMark: isFalsy(markCompetence) ? 0 : markCompetence!.currentMark, maxMark: c.maxMark, 
    //           mark: isFalsy(markCompetence) ? 0 : markCompetence!.mark, 
    //           rank: isFalsy(markCompetence) ? 0 : markCompetence!.rank, 
    //           markExist: isFalsy(markCompetence) ? true : markCompetence!.markExist, 
    //           markPurpose: isFalsy(markCompetence) ? '' : markCompetence!.markPurpose, 
    //           markAppreciationId: isFalsy(markCompetence) ? -1 : markCompetence!.markAppreciationId,
              
    //           schoolYearTemplateSubjectExamScheduleCompetenceId: c.id,
    //           schoolYearTemplateSubjectCompetenceDescription: c.schoolYearTemplateSubjectCompetenceDescription,

    //           firstName: registrationMark.firstName, lastName: registrationMark.lastName,
    //           schoolYearTemplateSubjectCompetenceTitle: c.title
    //         }; 
    //       }) as unknown as IRegistrationMarkCompetence[];

    //     setValue(`registrationMarks.${indexRegistrationMark}.registrationMarkCompetences`, registrationMarkCompetences);
    //   } else
    //       errorLogs.push(`${t('Marks')} :  ${t('Invalid data !!!')} ${firstName} ${lastName}`);
    // }
    
    // for(const competence of 
    //         sheetRegistrationMarkCompetences.filter(c => (!isFalsy(c.firstName) || !isFalsy(c.lastName)) 
    //                                               && !isFalsy(c.schoolYearTemplateSubjectCompetenceTitle)) ) {

    //   const {firstName, lastName, schoolYearTemplateSubjectCompetenceTitle, mark} = competence;
            
    //   const indexRegistrationMark = registrationMarks.findIndex(r => 
    //       r.firstName.trim() === firstName.trim() && r.lastName.trim() === lastName.trim());

    //   if(indexRegistrationMark>=0) {
    //     const registrationMark = registrationMarks[indexRegistrationMark];
        
    //     const indexCompetence = registrationMark.registrationMarkCompetences.findIndex(c => 
    //         c.schoolYearTemplateSubjectCompetenceTitle.trim() === schoolYearTemplateSubjectCompetenceTitle.trim());
    //     if(indexCompetence >= 0) {
    //       setValue(`registrationMarks.${indexRegistrationMark}.` +
    //                 `registrationMarkCompetences.${indexCompetence}.mark` as any, mark);
    //     } else
    //         errorLogs.push(`${t('Exam')}-${t('Competence')} :  ${t('Invalid data !!!')} ${schoolYearTemplateSubjectCompetenceTitle}`);
    //   } else
    //     errorLogs.push(`${t('Mark')}-${t('Competence')} :  ${t('Invalid data !!!')} ${firstName} ${lastName}`);

    // }

    // if(errorLogs.length > 0)
    //   displayDownloadErrorLogs(t('Importation file contains errors'), 'error', `${t('Marks')}-${getValues().schoolYearClassName}-${refSchoolYearPeriodExamName.current}-${getValues().schoolYearTemplateSubjectLongName}.txt`, errorLogs )

    setOpenBasicImport2Form(false);
  }

  const studentDisciplinaryRowActionIcon = ( registrationMark: IStudentDisciplinary) : ActionIconTableRow<ISchoolYearClassDisciplinaryMark,IStudentDisciplinary> => {
  
    const res: ActionIconTableRow<ISchoolYearClassDisciplinaryMark,IStudentDisciplinary> = {
      toolTip: 'more',
      icon: MoreVertIcon,
      hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event : any,index: number, row: IStudentDisciplinary) => {     

        //if(isFalsy(schoolYearClassTemplate)) return;

        // const schoolYearClassSubject = schoolYearClassSubjects.find( s => s.id === watchSchoolYearClassSubjectId);
        // if(isFalsy(schoolYearClassSubject)) return;

        // const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
        //         s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
        // if(isFalsy(schoolYearTemplateSubject)) return;

        // const schoolYearTemplateSubjectExamSchedule =  schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find( 
        //   e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId );

        // const regMark = getValues().registrationMarks.at(index);
        // if(isFalsy(regMark)) return;

        // const registrationMarkCompetences = schoolYearTemplateSubjectExamSchedule?.schoolYearTemplateSubjectExamScheduleCompetences.map(
        //   (c) => {

        //     const markCompetence = regMark.registrationMarkCompetences.find(x => x.schoolYearTemplateSubjectExamScheduleCompetenceId === c.id);

        //     return {...c, 
        //       id: isFalsy(markCompetence) ? 0 : markCompetence!.id,

        //       registrationMarkId: regMark.id, status: 'N', 
        //       currentMark: isFalsy(markCompetence) ? 0 : markCompetence!.currentMark, maxMark: c.maxMark, 
        //       mark: isFalsy(markCompetence) ? 0 : markCompetence!.mark, 
        //       rank: isFalsy(markCompetence) ? 0 : markCompetence!.rank, 
        //       markExist: isFalsy(markCompetence) ? true : markCompetence!.markExist, 
        //       markPurpose: isFalsy(markCompetence) ? '' : markCompetence!.markPurpose, 
        //       markAppreciationId: isFalsy(markCompetence) ? -1 : markCompetence!.markAppreciationId,
              
        //       schoolYearTemplateSubjectExamScheduleCompetenceId: c.id,
        //       schoolYearTemplateSubjectCompetenceDescription: c.schoolYearTemplateSubjectCompetenceDescription,

        //       firstName: regMark.firstName, lastName: regMark.lastName,
        //       schoolYearTemplateSubjectCompetenceTitle: c.title
        //     }; 
        // }) as unknown as IRegistrationMarkCompetence[];

        //setValue(`registrationMarks.${index}.registrationMarkCompetences`, registrationMarkCompetences);
          
        setStudentIndex(index);   
        setOpenStudentAbsencePurpose(true);         
      }
    }
    return res;
}


const cellKeyDownMarkCompetence = (e: React.KeyboardEvent<HTMLInputElement>, row: IRegistrationMarkCompetence,index: number) => {
    
  if( !['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key) )
    return;

  const cellId = 'mark';
  const rowsPathNameDef = 'studentDisciplinaries'; /// to do: look it after ....
  const len = getValues().studentDisciplinaries.length;

  const idx = ['Enter', 'ArrowDown'].includes(e.key) ? 
                Math.min(index+1, len-1) : Math.max(index-1, 0);
  
  const elt = document.getElementById(`${rowsPathNameDef}-id-${cellId}-${idx}`); // This is how id is set in ArrayFeldTableEx
  if(isFalsy(elt))
    return;

  elt!.focus();
  (elt! as HTMLInputElement).select();    
}

const cellEditableRegistrationMarkCompetence = (row: IRegistrationMarkCompetence, cellId: keyof IRegistrationMarkCompetence) => {
   
  // const registrationMark = getValues().registrationMarks.find(x => x.id === row.registrationMarkId);
  // if(isFalsy(registrationMark)) return false;

  // if(cellId === 'markExist' && registrationMark.markExist) return true;

  // return row.markExist 
  //         && ['00','95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus) ;
  return true;
}

const cellAllowedMarkCompetence = (row: IRegistrationMarkCompetence, valueMark: number) => {
  
  if(valueMark > row.maxMark)
    enqueueSnackbar( `${t('Invalid mark, the mark must be less than the max allowed' )} : ${row.maxMark} !!!`, { variant: 'warning',
      anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 

  const allowed = valueMark >= 0 && valueMark <= row.maxMark;
  //debouncedMark();

  return allowed;  
}

const [headRegistrationMarkCompetenceCells, setHeadRegistrationMarkCompetenceCells]  = useState<HeadCell<IRegistrationMarkCompetence>[]>([]);
  useEffect(() => {
    setHeadRegistrationMarkCompetenceCells([            
      {id:'id', label : t('Id'),  display: false, type: 'numeric', },

      {id:'schoolYearTemplateSubjectCompetenceDescription', label : t('Competence'),  display: true, type: 'string', width: 40, },
      {id:'status', label : t('Status'),  display: true, type: 'string', width: 7, },

      {id:'currentMark', label : t('Cur.'),  display: true, type: 'numeric', width: 7, decimalScale: 2 },
      {id:'mark', label : t('Mark'),  display: true, type: 'numeric', width: 7, decimalScale: 2, 
        isEditable: cellEditableRegistrationMarkCompetence, handleKeyDown: cellKeyDownMarkCompetence, 
          isAllowed: cellAllowedMarkCompetence },
      {id:'markExist', label : t('Exist ?'),  display: true, type: 'boolean', width: 12, 
        isEditable: cellEditableRegistrationMarkCompetence },
      {id:'rank', label : t('Rank'),  display: true, type: 'numeric', width: 7 },

      {id:'markPurpose', label : t('Purpose'),  display: true, type: 'numeric', width: 20 },
    ]  )
  }, [t,i18n])

  const refAppendRegistrationMarkCompetences = useRef<(value: Partial<FieldArray<ISchoolYearClassSubjectMark>> | Partial<FieldArray<ISchoolYearClassSubjectMark>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateRegistrationMarkCompetence = useRef<(index: number,value: Partial<FieldArray<ISchoolYearClassSubjectMark>> ) => void>(null);
  const refRemoveRegistrationMarkCompetence = useRef<(index: number ) => void>(null);


  const handleClickOpenSchoolYearClass = (event: any) => {      
    setOpenClassFilter(true);      
  }

  
  const getSubjectExamStatusList = (row: ISchoolYearClassSubjectExamSchedule, cellId: keyof ISchoolYearClassSubjectExamSchedule, 
    opts: {value: string, name: string}[]) => {        
    
    return getAsOptions(refEnumItems.current ?? [], '');
  }

  const [openSchoolYearClassSubjectExamSchedule, setOpenSchoolYearClassSubjectExamSchedule] = useState<boolean>(false);
  const handleOpenSchoolYearClassSubjectExamSchedule = async (event: any) => {

    // const arr = await getSchoolYearClassSubjectExamSchedules(watchSchoolYearClassId, watchSchoolYearPeriodId ,watchSchoolYearPeriodExamId);

    // setSchoolYearClassSubjectExamSchedules(arr);   
    setOpenSchoolYearClassSubjectExamSchedule(true);
  };

  const [studentIndex, setStudentIndex] = useState<number>(-1);
  const [openStudentAbsencePurpose, setOpenStudentAbsencePurpose] = useState(false);
  const handleClickOpenMarkPurpose = (event: any) => {      
    setOpenStudentAbsencePurpose(true);      
  }

  const refEnumItems = useRef<IEnumerationItem[]>();    
    useEffect( () => {   
        refEnumItems.current = enumItems;     
    }, [enumItems]);


useEffect( () => {   
  async function _() {
    
    if(watchSchoolYearId <= 0) return;

    const schoolYear = schoolYears.find(x => x.id === watchSchoolYearId);
    if(isFalsy(schoolYear)) return;

    setValue('schoolYearStartDate', schoolYear.startDate);
    setValue('schoolYearEndDate', schoolYear.endDate);

    const periods = await schoolYearServiceRef.current.getSchoolYearPeriods(watchSchoolYearId);    
    setSchoolYearPeriods([...periods]);

    // const exams = await getSchoolYearPeriodExams(watchSchoolYearId);    
    // setSchoolYearPeriodExams([...exams]);    
  }          

   _();

  }, [watchSchoolYearId]);


  // useEffect( () => {              
  //   if(watchSchoolYearClassSubjectId <= 0) return;

  //   const schoolYearClassSubject = schoolYearClassSubjects.find(x => x.id === watchSchoolYearClassSubjectId);
  //   if(isFalsy(schoolYearClassSubject)) return;

  //   setValue('teacherName', schoolYearClassSubject.teacherName);
  //   setValue('schoolYearTemplateSubjectLongName', schoolYearClassSubject.schoolYearTemplateSubjectLongName);
  // }, [watchSchoolYearClassSubjectId]);


  const refSchoolYearPeriodExamName = useRef<string>('');
  // useEffect( () => {              
  //   async function _() {
      
  //     const data = getValues(); 

  //     setValue('schoolYearClassSubjectId', 0);

  //     if(data.schoolYearClassId <= 0 || data.schoolYearClassTemplateId <= 0) {
  //       reset({...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: []});
  //       return;
  //     }

  //     if(watchSchoolYearPeriodExamId <= 0) {
  //       reset({...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: []});
  //       return;
  //     }

  //     const schoolYearPeriodExam = schoolYearPeriodExams.find(e => e.id === watchSchoolYearPeriodExamId);
  //     if(isFalsy(schoolYearPeriodExam)) {
  //       reset({...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: []});
  //       return;
  //     }

  //     refSchoolYearPeriodExamName.current = schoolYearPeriodExam.name;
    
      
  //     // we load subject in case of teacher when exam change.
  //     if(isTeacher) {
  //       const arr = await getSchoolYearClassSubjectsByPeriodExamTeacher(watchSchoolYearClassId, watchSchoolYearPeriodExamId, teacherId); // schoolYearClassId
  //       setSchoolYearClassSubjects([...arr]);
  //     }  

  //   }          
  
  //    _();

    
  // }, [watchSchoolYearPeriodExamId]);


  useEffect( () => {   
    async function _() {
      
      const data = getValues();

      console.log({watchSchoolYearId, watchSchoolYearClassId,  watchSchoolYearPeriodId});
      
      reset({...data, studentDisciplinaries: [] });
      
      if(data.schoolYearClassId <= 0) {
        reset({...data, studentDisciplinaries: []});
        return;
      } 

      if(watchSchoolYearPeriodId <= 0) {
        // enqueueSnackbar( `${t('You have to select exam and subject' )} !!!`, { variant: 'info',
        //   anchorOrigin : { horizontal: 'left', vertical: 'bottom' }, autoHideDuration : 500 }); 
        reset({...data, studentDisciplinaries: []});
        return;
      } 
      
      
      // const schoolYearClassSubjectExamSchedule = schoolYearClassSubjectExamSchedules.find(
      //       x => x.schoolYearTemplateSubjectId === schoolYearClassSubject.schoolYearTemplateSubjectId &&
      //            x.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);
                       
      // if(isFalsy(schoolYearClassSubjectExamSchedule)) {         
      //   enqueueSnackbar( `${t('This subject have not been scheduled for this exam' )} `, { variant: 'warning',
      //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
      //   reset({...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00',  registrationMarks: []});
      //   return;
      // }
      
      //const {maxMark, status, id} = schoolYearClassSubjectExamSchedule;
      const studentDisciplinaries = await schoolYearServiceRef.current.getBasicStudentDisciplinaries(
                                            watchSchoolYearId, watchSchoolYearClassId, watchSchoolYearPeriodId);
      
      //_setId(id);
      reset({...data, studentDisciplinaries});
        
    }          
  
     _();
  
    }, [watchSchoolYearId, watchSchoolYearClassId, watchSchoolYearPeriodId]);


  useEffect( () => {              
    setCurrentFormNameAtom(t('Mark'));  
    setCurrentBasicTextFilterProps(defaultBasicTextFilterProps);
  }, []);

  

  /********** This use effect call retrieve data wich will call refetch and _data will be updated. 
and the new useEffect will take place ********************/
useEffect( () => {
    // setCurrentFormName(t('Billing'));        
    
    // if(_id > 0)
    //   retrieveData('ApplicationQuery',_id, refetch);  
  }, [_id] );


useEffect( () => {
   
  // if(_data && _data.id > 0) {
  //   reset(_data);
  // }
}, [_data]);


const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
  _setId(0);           
  reset(defaultSchoolYearClassSubjectMark);    
}

const saveData = async (event: MouseEvent<HTMLButtonElement>) => {    
  if(!checkEntitySaveAuthorization('SchoolYearClassDisciplinaryMark', _id)){
    setIsSaveLoading(false);
    return;
  }   

  const data = getValues(); 

  if(data.studentDisciplinaries.length <= 0){
    enqueueSnackbar( t('There is no mark to save'), { variant: 'warning',
      anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
      setIsSaveLoading(false);
      return;
  }

  // const schoolYearTemplateSubjectExamScheduleIds = 
  //   Array.from( new Set( data.studentDisciplinaries.map( s => s.schoolYearTemplateSubjectExamScheduleId ) || []) );
  // if(schoolYearTemplateSubjectExamScheduleIds.length !== 1) {
  //   enqueueSnackbar( t('All marks did not belong to the same exam'), { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // } 

  // if(data.studentDisciplinaries.some(x => x.schoolYearTemplateSubjectExamScheduleId <= 0)){
  //   enqueueSnackbar( t('There is an error in the selection of exam'), { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }


  // if(isFalsy(schoolYearClassTemplate)) {
  //   enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Class template')}`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }
    
  // const schoolYearClassSubject = schoolYearClassSubjects.find( s => s.id === watchSchoolYearClassSubjectId);
  // if(isFalsy(schoolYearClassSubject)) {
  //   enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Subject')}`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }
    
  // const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
  //         s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
  // if(isFalsy(schoolYearTemplateSubject)) {
  //   enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Subject')} +++`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }
    
  // const schoolYearTemplateSubjectExamSchedule =  schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find( 
  //   e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId );
    
  // if(isFalsy(schoolYearTemplateSubjectExamSchedule)) {
  //   enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Exam')} +++`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }
    

  // if(data.registrationMarks.some( r => r.mark !== 0 && 
  //     r.registrationMarkCompetences.length !== schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length ) ) {
  //       enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Competence')} ###`, { variant: 'warning',
  //         anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //         setIsSaveLoading(false);
  //         return;
  //   }

    // if(data.registrationMarks.some( r => r.mark !== 0 && 
    //   schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length !== 0 &&
    //   r.registrationMarkCompetences.filter(c => c.markExist).length === 0 ) ) {
    //     enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Competence')} ### ${t('Exist ?')}`, { variant: 'warning',
    //       anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
    //       setIsSaveLoading(false);
    //       return;
    // }

    // if(data.registrationMarks.some( r => !r.markExist && r.registrationMarkCompetences.some(c => c.markExist) ) ) {
    //     enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Competence')} == ${t('Exist ?')}`, { variant: 'warning',
    //       anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
    //       setIsSaveLoading(false);
    //       return;
    // }

  
  // if( schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.some(c => c.maxMark <= 0) ) {
  //     enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Competence')} - - ${t('Max mark')}`, { variant: 'warning',
  //       anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //       setIsSaveLoading(false);
  //       return;
  // }    

  // const { maxMark } = data;

  // if( data.registrationMarks.some(c => maxMark < c.mark) ) {
  //   enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Max')} / ${t('Mark')}`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }

  // if( data.registrationMarks.some(r => r.registrationMarkCompetences.some(c => c.maxMark < c.mark) ) ) {
  //   enqueueSnackbar( `${t('Invalid data !!!')} - ${t('Competence')} - ${t('Max')} / ${t('Mark')}`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     setIsSaveLoading(false);
  //     return;
  // }

  
  // let sumMaxMark = 0;
  // if( schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length !== 0 &&
  //     data.registrationMarks.some( r => r.mark>0 && 
  //        ( sumMaxMark = r.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.maxMark , 0) ) !== 0 &&
  //        Math.abs( r.mark -  
  //             (r.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.mark , 0)*maxMark)/sumMaxMark ) > 0.0001)) {
  //         enqueueSnackbar( `${t('Invalid data !!!')}  - ${t('Mark')} / ${t('Competence')} `, { variant: 'warning',
  //           anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //           setIsSaveLoading(false);
  //           return;
  //     }


    // const sumMark = row.registrationMarkCompetences.reduce((acc, curr) => acc + curr.mark , 0);
    // const sumMaxMark = row.registrationMarkCompetences.reduce((acc, curr) => acc + curr.maxMark , 0);
    
    // const markCompetence = (sumMark*maxMark)/sumMaxMark;

  //const schoolYearTemplateSubjectExamScheduleId = schoolYearTemplateSubjectExamScheduleIds[0];

  //console.log({data});
  
  mutate( {...data });
}

const actionData = async (event: MouseEvent<HTMLButtonElement>) => {  
  openEntityActionDrawer('SchoolYearClassSubjectExamSchedule', _id);
}



const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {       
  const data = getValues();

  // const arr = await getSchoolYearClassSubjectExamSchedules(watchSchoolYearClassId, watchSchoolYearPeriodId ,watchSchoolYearPeriodExamId);

  // setSchoolYearClassSubjectExamSchedules(arr);

  // const schoolYearClassSubject = schoolYearClassSubjects.find(x => x.id === watchSchoolYearClassSubjectId);
  // if(isFalsy(schoolYearClassSubject)) {
  //   enqueueSnackbar( `${t('This subject is not valid in this context' )} !!!`, { variant: 'warning',
  //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //   return;
  // }

  // const schoolYearClassSubjectExamSchedule = arr.find(
  //   x => x.schoolYearTemplateSubjectId === schoolYearClassSubject.schoolYearTemplateSubjectId &&
  //        x.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);
         
  //   if(isFalsy(schoolYearClassSubjectExamSchedule)) {         
  //     enqueueSnackbar( `${t('This subject have not been scheduled for this exam' )} `, { variant: 'warning',
  //       anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
  //     return;
  //   }

  //   const {maxMark, status, id} = schoolYearClassSubjectExamSchedule;
       
  //   _setId(id);
  //   reset({...data, maxMark, schoolYearClassSubjectExamScheduleStatus: status});
}

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={1.5} columnSpacing={1.5}>
                    <Grid item xs={12} md={4} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                        <Stack flexDirection='column'  >
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <Typography  variant="h6" id="tableTitle" color="primary" noWrap sx={{...typographyGroupBoxStyling}}>
                                {`${t('Class')} ...`}
                              </Typography>                              
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >

                                <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                                <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                                <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                                <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />

                                <TextField sx={{width:'calc(50% - 8px)'}} id="schoolYearClassName" 
                                    label={t('Class')} {...register('schoolYearClassName')} inputProps={ {readOnly: true}} 
                                    InputProps={{
                                      readOnly: true,
                                      endAdornment: (
                                        <InputAdornment position="end">                                            
                                          <IconButton color="primary" onClick={handleClickOpenSchoolYearClass}>
                                            <ArrowDropDownCircleIcon />
                                          </IconButton>                                                                                               
                                      </InputAdornment>
                                    ) 
                                  }}
                                  helperText={ (getValues().schoolYearClassId) > 0 ?
                                              <FormHelperText>                                    
                                                <Link
                                                  href="#"
                                                  onClick={handleClassNavigationClick}
                                                  sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                                                >
                                                  {t('Class details')}
                                                </Link>
                                              </FormHelperText>: null
                                            } />
                                <Controller name='studyType' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyType"
                                        label={t('Study type')} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_STUDY_TYPE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />

                                { openClassFilter && <FormDialog open={openClassFilter} maxWidth='md'
                                    okText={''} cancelText={t('Cancel')} title={t('Classes')} onCancel={()=> {setOpenClassFilter(false);}} 
                                    onClose={()=> {setOpenClassFilter(false);}} onOk={()=> {}}  >
                                        <BasicTextFilterForm<ISchoolYearClass> {...basicFilterSchoolYearClass } />
                                </FormDialog> }
                                
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(50% - 8px)'}} id="classTemplateName" 
                                    label={t('Class template')} {...register('classTemplateName')} inputProps={ {readOnly: true}}  />
                              <Controller name='studyLanguage' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="studyLanguage"
                                        label={t('Study language')} inputProps={ {readOnly: true}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_STUDY_LANGUAGE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box> 
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(50% - 8px)'}} id="fullTeacherName" 
                                    label={t('Full teacher')} {...register('fullTeacherName')} inputProps={ {readOnly: true}}  />
                              <TextField sx={{width:'calc(50% - 8px)'}} id="substituteTeacherName" 
                                    label={t('Substitute teacher')} {...register('substituteTeacherName')} inputProps={ {readOnly: true}}  />
                            </Box>
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <Typography  variant="h6" id="tableTitle" color="primary" noWrap sx={{...typographyGroupBoxStyling}}>
                                {`${t('Exam')} ...`}
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Controller name='schoolYearId' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="schoolYearId"
                                        label={t('School year')} inputProps={ {readOnly: false}} focused >
                                        {schoolYears && schoolYears.map( 
                                          (x,idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                <Controller control={control}
                                  name='schoolYearStartDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Start date')} 
                                      onChange={onChange} disableOpenPicker readOnly                   
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(25% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                                    /> )}
                                />
                                
                                <Controller control={control}
                                  name='schoolYearEndDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('End date')} 
                                      onChange={onChange} disableOpenPicker readOnly               
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(25% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                                    /> )}
                                /> 
                            </Box> 
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Controller name='schoolYearPeriodId' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="schoolYearId"
                                        label={t('Period')} inputProps={ {readOnly: false}} focused >
                                        {schoolYearPeriods && schoolYearPeriods.map( 
                                          (x,idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                {/* <Controller name='schoolYearPeriodExamId' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="schoolYearId"
                                        label={t('Exam')} inputProps={ {readOnly: false}} focused >
                                        {schoolYearPeriodExams && schoolYearPeriodExams.filter(x => x.schoolYearPeriodId === watchSchoolYearPeriodId).map( 
                                          (x,idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> */}
                            </Box>
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <Typography  variant="h6" id="tableTitle" color="primary" noWrap sx={{...typographyGroupBoxStyling}}>
                                {`${t('Subject')} ...`}
                              </Typography>
                            </Box>
                             
                            <Box sx={{ mt: 4, width: '100%' }} >
                              {/* <Controller 
                                  name={`methodName`}
                                  control={control}
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(100% - 8px)'}} id="methodName"
                                      label={t('Data preparation method')} inputProps={ {readOnly: false}}>                                
                                      {currentLibraryClass.externalLibraryClassMethods.map( 
                                          (x,idx) => <MenuItem key={x.methodName} value={x.methodName}>{x.methodName}</MenuItem> )
                                        }
                                    </TextField>
                                  )}
                              /> */}
                            </Box>
                        </Stack>                        
                    </Grid>                   
                    <Grid item xs={12}  md={8} component={Paper} >
                        <Stack flexDirection='column'>     
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <Controller name='justifiedAbsenceType' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="justifiedAbsenceType"
                                        label={`${t('Absence type')} : ${t('Jusified')}`} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_ABSENCE_TYPE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                <Controller name='justifiedAbsencePurpose' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(24% - 8px)'}} id="justifiedAbsencePurpose"
                                        label={`${t('Absence purpose')} : ${t('Jusified')}`} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_ABSENCE_PURPOSE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                {/* <Divider orientation='vertical' /> */}
                                <Controller name='unjustifiedAbsenceType' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="justifiedAbsenceType"
                                        label={`${t('Absence type')} : ${t('Unjusified')}`} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_ABSENCE_TYPE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                <Controller name='unjustifiedAbsencePurpose' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(24% - 8px)'}} id="justifiedAbsencePurpose"
                                        label={`${t('Absence purpose')} : ${t('Unjusified')}`} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_ABSENCE_PURPOSE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box>                       
                            <Box sx={{ mt: 1, width: '100%' }} 
                                key={` ${watchSchoolYearPeriodId} - 
                                    ${getValues().studentDisciplinaries.map(a => String(a.fullNameEN)).join('-')} --
                                    ${getValues().studentDisciplinaries.map(a => String(a.fullNameFR)).join('-')}   `} >
                              <ArrayFieldTableEx<ISchoolYearClassDisciplinaryMark,IStudentDisciplinary,'_id'> 
                                mainObject={getValues()} fieldKey='fullNameFR' 
                                headCells={headStudentDisciplinaryCells} rowsPathName={`studentDisciplinaries` }
                                title={`${t('Student mark out of')} `} 
                                rowActionIcon={studentDisciplinaryRowActionIcon}  
                                //onRowSelected={handleQuerySelected}
                                                    
                                refAppend={refAppendStudentDisciplinaries as MutableRefObject<(value: Partial<FieldArray<ISchoolYearClassDisciplinaryMark>> | Partial<FieldArray<ISchoolYearClassDisciplinaryMark>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateStudentDisciplinary as MutableRefObject<(index: number,value: Partial<FieldArray<ISchoolYearClassDisciplinaryMark>>) => void>}
                                refRemove={refRemoveStudentDisciplinary as MutableRefObject<(index: number) => void>}

                                rowCheckedMode='multiple'
                                //onRowCheckedSelectChange={handleRowCheckedSelectChange}
                                stateSelectedIndex={[selectedRegistrationsIndex, setSelectedRegistrationsIndex]}
                                //displayMore={undefined}
                                toolbarActions={[
                                { toolTip: `${t('Set same mark for selected students')}...`, onClickIcon: handleSetMarkForSelected ,icon: CheckBoxIcon,  },
                                { toolTip: `${t('Set same mark for all students')}...`, onClickIcon: handleSetMarkForAll ,icon: SelectAllIcon,  },

                                { toolTip: `${t('Download marks as Excel file and prepare for upload')}...`, onClickIcon: handleDownloadMarks,icon: FileDownloadIcon,  },

                                { toolTip: `${t('Upload marks from file')}...`, onClickIcon: handleLoadMarks,icon: FileUploadIcon,  },

                                
                                ]}
                              />        
                              
                              { openStudentAbsencePurpose && (studentIndex >= 0) && <FormDialog open={openStudentAbsencePurpose} maxWidth='md' height='35vh'
                                    okText={t('OK')} cancelText={''} title={`${t('Mark purpose')} / ${t('Marks per competence')}`} 
                                    onCancel={()=> {setOpenStudentAbsencePurpose(false);}} 
                                    onClose={()=> {setOpenStudentAbsencePurpose(false);}} onOk={()=> {setOpenStudentAbsencePurpose(false);}}  >
                                      <Stack flexDirection='column'>                            
                                        <Box sx={{ mt: 1, width: '100%' }} >
                                           <Controller name={`studentDisciplinaries.${studentIndex}.justifiedAbsenceType`} control={control}                                     
                                              render={ ({field: {onChange, value}}) => (
                                                <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="justifiedAbsenceType"
                                                  label={`${t('Absence type')} : ${t('Jusified')}`} inputProps={ {readOnly: false}} focused >
                                                  {enumItems && enumItems.filter( e => 
                                                        e.enumerationCode === Enum_ABSENCE_TYPE ).map( 
                                                    (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                  }
                                                </TextField>
                                              )}
                                          />
                                          <Controller name={`studentDisciplinaries.${studentIndex}.justifiedAbsencePurpose`} control={control}                                     
                                              render={ ({field: {onChange, value}}) => (
                                                <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="justifiedAbsencePurpose"
                                                  label={`${t('Absence purpose')} : ${t('Jusified')}`} inputProps={ {readOnly: false}} focused >
                                                  {enumItems && enumItems.filter( e => 
                                                        e.enumerationCode === Enum_ABSENCE_PURPOSE ).map( 
                                                    (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                  }
                                                </TextField>
                                              )}
                                          />                                         
                                        </Box>
                                        <Box sx={{ mt: 1, width: '100%' }} >
                                          <Controller name={`studentDisciplinaries.${studentIndex}.unjustifiedAbsenceType`} control={control}                                     
                                              render={ ({field: {onChange, value}}) => (
                                                <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="unjustifiedAbsenceType"
                                                  label={`${t('Absence type')} : ${t('Unjusified')}`} inputProps={ {readOnly: false}} focused >
                                                  {enumItems && enumItems.filter( e => 
                                                        e.enumerationCode === Enum_ABSENCE_TYPE ).map( 
                                                    (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                  }
                                                </TextField>
                                              )}
                                          />
                                          <Controller name={`studentDisciplinaries.${studentIndex}.unjustifiedAbsencePurpose`}  control={control}                                     
                                              render={ ({field: {onChange, value}}) => (
                                                <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="unjustifiedAbsencePurpose"
                                                  label={`${t('Absence purpose')} : ${t('Unjusified')}`} inputProps={ {readOnly: false}} focused >
                                                  {enumItems && enumItems.filter( e => 
                                                        e.enumerationCode === Enum_ABSENCE_PURPOSE ).map( 
                                                    (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                  }
                                                </TextField>
                                              )}
                                          />
                                        </Box>
                                      </Stack>    
                                </FormDialog> }

                                { openSameMarkDialog && <FormDialog open={openSameMarkDialog} maxWidth='xs' height='35vh'
                                    okText={t('OK')} cancelText={t('Cancel')} title={t('Set same mark')} onCancel={()=> {setOpenSameMarkDialog(false);}} 
                                    onClose={()=> {setOpenSameMarkDialog(false);}} onOk={handleSetMarkOk}  >
                                      <Stack flexDirection='column'>                            
                                        <Box sx={{ mt: 1, width: '100%' }} key={` ... `} >
                                          
                                                <NumberFormat    
                                                  label={t('Mark')} sx={{width:'calc(100% - 8px)'}}
                                                  allowEmptyFormatting={false} 
                                                  control={control}    
                                                  thousandSeparator={true}
                                                  decimalScale={2}
                                                  onValueChange={ (v) => setSameMark(v.floatValue||0) }
                                                  defaultValue={sameMark}
                                                  value={sameMark}
                                                  customInput={TextFieldRight}                            
                                                />
                                              
                                          
                                        </Box>
                                      </Stack>    
                                </FormDialog> }   

                                { openBasicImport2Form && <FormDialog open={openBasicImport2Form} maxWidth='sm' height='45vh'
                                  okText={t('Import')} cancelText={t('Cancel')} title={`${t('Import')} ....`} onCancel={()=> {setOpenBasicImport2Form(false);}} 
                                  onClose={()=> {setOpenBasicImport2Form(false);}} onOk={handleOKBasicImport2Marks}  >
                                      <BasicImport2Form<IRegistrationMark,IRegistrationMarkCompetence> 
                                        //fileNameToLoad={`${t('Marks')}-${getValues().schoolYearClassName}-${refSchoolYearPeriodExamName.current}-${getValues().schoolYearTemplateSubjectLongName}.xlsx`}
                                        fileNameToLoad={`${t('Students')}-${getValues().schoolYearClassName}-${getValues().schoolYearPeriodId}.xlsx`}
                                        stateItems={[markObjecBasicImport2, setMarkObjecBasicImport2]} 
                                        columns={ {
                                          first: ['firstName','lastName','mark'],
                                          second: ['firstName','lastName','schoolYearTemplateSubjectCompetenceTitle','mark'],                                          
                                        }}
                                        defaultItem={{first: defaultRegistrationMark, second: defaultRegistrationMarkCompetence }}
                                        
                                        sheetName={{
                                          first: `${t('Mark')}`, 
                                          second: `${t('Mark')}-${t('Competences')}` }}
                                        />
                                </FormDialog> }         
                            </Box>                            
                        </Stack> 
                    </Grid>
                </Grid>
            </Box>
        </FormProvider>
  )
}
